import {
  ILoyaltyServiceQuestFragment,
  IQuestFragment,
  QuestFragmentDoc,
  useActivateQuestMutation,
  useLoyaltyServiceActivateQuestMutation,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import logger from 'utils/logger';

const getQuestCacheId = (questId: string) => `LoyaltyQuest:${questId}`;

type LegacyQuestStep = NonNullable<NonNullable<IQuestFragment['steps']>[number]>;

const toLegacyQuestFragment = ({
  cachedQuest,
  questId,
  serviceQuest,
}: {
  cachedQuest: IQuestFragment | null;
  questId: string;
  serviceQuest: ILoyaltyServiceQuestFragment;
}): IQuestFragment => {
  const cachedStepsById = new Map(cachedQuest?.steps?.map(step => [step.id, step]));

  return {
    __typename: 'LoyaltyQuest',
    id: questId,
    type: (serviceQuest.type as unknown) as IQuestFragment['type'],
    bonusPoints: serviceQuest.bonusPoints,
    startDate: serviceQuest.startDate,
    endDate: serviceQuest.endDate,
    sanityId: serviceQuest.sanityId ?? cachedQuest?.sanityId ?? null,
    externalIdentifiers: cachedQuest?.externalIdentifiers ?? null,
    lastCompletion: serviceQuest.lastCompletion
      ? {
          __typename: 'LoyaltyQuestCompletion',
          benefitId: serviceQuest.lastCompletion.benefitId,
          completedAt: serviceQuest.lastCompletion.completedAt,
          redeemed: serviceQuest.lastCompletion.redeemed,
        }
      : null,
    incentiveType: (serviceQuest.incentiveType as unknown) as IQuestFragment['incentiveType'],
    status: (serviceQuest.status as unknown) as IQuestFragment['status'],
    steps:
      serviceQuest.steps?.map(step => ({
        __typename: 'LoyaltyQuestStep',
        id: step.id,
        status: (step.status as unknown) as LegacyQuestStep['status'],
        cartRequirements:
          step.cartRequirements?.map(
            requirements =>
              requirements?.map(requirement =>
                requirement
                  ? {
                      __typename: 'LoyaltyCartRequirement',
                      id: requirement.id,
                      quantity: requirement.quantity,
                    }
                  : null
              ) ?? null
          ) ?? null,
        field: step.field,
        bonusPoints: step.bonusPoints,
        tierIndex: cachedStepsById.get(step.id)?.tierIndex ?? null,
      })) ?? null,
    cmsType: (serviceQuest.cmsType as unknown) as IQuestFragment['cmsType'],
    moreInfo: serviceQuest.moreInfo,
  };
};

export const useActivateQuest = ({ questId }: { questId: string | undefined }) => {
  const enableLoyaltyServiceQuest = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_QUEST));
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;

  // this mutation leverages from the Apollo's cache to update the proper Loyalty Quest object
  const legacyActivateQuestMutation = useActivateQuestMutation({
    variables: {
      questId,
      loyaltyId: loyaltyId!,
    },
    onError: errors => {
      logger.error(`Error activating quest: ${questId} ${JSON.stringify(errors)}`);
    },
    update(cache, { data }) {
      const activeQuest = data?.loyaltyActivateQuest;

      if (activeQuest && questId) {
        cache.writeFragment({
          id: getQuestCacheId(questId),
          fragment: QuestFragmentDoc,
          data: {
            ...activeQuest,
            // the id changes bc a personalized quest is created when it's activated
            id: questId,
          },
          fragmentName: 'QuestFragment',
        });
      }
    },
  });

  const loyaltyServiceActivateQuestMutation = useLoyaltyServiceActivateQuestMutation({
    variables: {
      input: {
        questId: questId!,
      },
    },
    onError: errors => {
      logger.error(`Error activating quest: ${questId} ${JSON.stringify(errors)}`);
    },
    update(cache, { data }) {
      const activeQuest = data?.loyaltyServiceActivateQuest;

      if (activeQuest && questId) {
        const questCacheId = getQuestCacheId(questId);
        const cachedQuest = cache.readFragment<IQuestFragment>({
          id: questCacheId,
          fragment: QuestFragmentDoc,
          fragmentName: 'QuestFragment',
        });

        cache.writeFragment({
          id: questCacheId,
          fragment: QuestFragmentDoc,
          data: toLegacyQuestFragment({
            cachedQuest,
            questId,
            serviceQuest: activeQuest,
          }),
          fragmentName: 'QuestFragment',
        });
      }
    },
  });

  return enableLoyaltyServiceQuest
    ? loyaltyServiceActivateQuestMutation
    : legacyActivateQuestMutation;
};
